/**
 * Keeps hydrated review controls addressable when responsive widget markup
 * contains the same control IDs more than once. Customer-facing copy and
 * behavior are untouched; only duplicate IDs and their local references move.
 */
(function reviewWidgetAccessibility() {
  const widgetSelector = [
    '[data-oke-widget]',
    '.okeReviews',
    '.okeReviews-widget',
    '[class*="okeReviews"]',
    '[class*="oke-reviews"]'
  ].join(',');
  const referenceAttributes = ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby'];
  let scheduled = false;
  let normalizing = false;

  function widgetScopes() {
    const scopes = new Set();
    document.querySelectorAll(widgetSelector).forEach((widget) => {
      scopes.add(
        widget.closest('.shopify-app-block, [id^="shopify-block-"]')
        || widget.closest('.shopify-section')
        || widget
      );
    });
    return Array.from(scopes);
  }

  function referencesFor(scope, originalId) {
    return Array.from(scope.querySelectorAll(referenceAttributes.map((attribute) => `[${attribute}]`).join(',')))
      .flatMap((element) => referenceAttributes
        .filter((attribute) => (element.getAttribute(attribute) || '').split(/\s+/).includes(originalId))
        .map((attribute) => ({ element, attribute })));
  }

  function nearestTargetIndex(reference, targets, positions) {
    const referencePosition = positions.get(reference) || 0;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    targets.forEach((target, index) => {
      const distance = Math.abs((positions.get(target) || 0) - referencePosition);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  function uniqueId(originalId, scopeIndex, duplicateIndex) {
    const stem = `${originalId}--an-${scopeIndex + 1}-${duplicateIndex + 1}`;
    let candidate = stem;
    let collision = 1;
    while (document.getElementById(candidate)) {
      collision += 1;
      candidate = `${stem}-${collision}`;
    }
    return candidate;
  }

  function normalizeScope(scope, scopeIndex, claimedIds) {
    const elements = Array.from(scope.querySelectorAll('[id]'));
    const positions = new Map(Array.from(scope.querySelectorAll('*')).map((element, index) => [element, index]));
    const groups = new Map();

    elements.forEach((element) => {
      const id = element.getAttribute('id');
      if (!id) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(element);
    });

    groups.forEach((targets, originalId) => {
      if (targets.length < 2 && !claimedIds.has(originalId)) {
        claimedIds.add(originalId);
        return;
      }
      const references = referencesFor(scope, originalId);
      const resolvedIds = targets.map((target, index) => {
        if (index === 0 && !claimedIds.has(originalId)) {
          claimedIds.add(originalId);
          return originalId;
        }
        const replacement = uniqueId(originalId, scopeIndex, index);
        target.setAttribute('id', replacement);
        claimedIds.add(replacement);
        return replacement;
      });

      references.forEach(({ element, attribute }, referenceIndex) => {
        const targetIndex = references.length === targets.length
          ? Math.min(referenceIndex, targets.length - 1)
          : nearestTargetIndex(element, targets, positions);
        const replacement = resolvedIds[targetIndex];
        const tokens = (element.getAttribute(attribute) || '').split(/\s+/).filter(Boolean);
        element.setAttribute(attribute, tokens.map((token) => token === originalId ? replacement : token).join(' '));
      });
    });
  }

  function normalize() {
    scheduled = false;
    if (normalizing) return;
    normalizing = true;
    try {
      const scopes = widgetScopes();
      const claimedIds = new Set(
        Array.from(document.querySelectorAll('[id]'))
          .filter((element) => !scopes.some((scope) => scope.contains(element)))
          .map((element) => element.id)
      );
      scopes.forEach((scope, scopeIndex) => normalizeScope(scope, scopeIndex, claimedIds));
    } finally {
      normalizing = false;
    }
  }

  function schedule() {
    if (scheduled || normalizing) return;
    scheduled = true;
    window.requestAnimationFrame(normalize);
  }

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => (
      mutation.type === 'childList'
      || mutation.attributeName === 'id'
      || referenceAttributes.includes(mutation.attributeName)
    ))) schedule();
  });

  function connect() {
    normalize();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', ...referenceAttributes]
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', connect, { once: true });
  } else {
    connect();
  }
  document.addEventListener('shopify:section:load', schedule);
  window.AnatomyReviewA11y = { normalize };
})();
